import { extractItemId, firstItemId } from './icons';
import {
  asArray,
  asRecord,
  unwrapBoolean,
  unwrapNumber,
  unwrapString,
  unwrapStringList,
} from './unwrap';

export type QuestShape =
  | 'circle'
  | 'square'
  | 'rsquare'
  | 'rounded_square'
  | 'diamond'
  | 'pentagon'
  | 'hexagon'
  | 'octagon'
  | 'heart'
  | 'gear'
  | 'none'
  | string;

export interface QuestTask {
  id: string | null;
  type: string;
  title: string | null;
  itemId: string | null;
  count: number | null;
  raw: Record<string, unknown>;
}

export interface QuestReward {
  id: string | null;
  type: string;
  title: string | null;
  itemId: string | null;
  count: number | null;
  raw: Record<string, unknown>;
}

export interface QuestNode {
  id: string;
  title: string;
  subtitle: string | null;
  description: string[];
  iconItemId: string | null;
  x: number;
  y: number;
  size: number;
  shape: QuestShape;
  optional: boolean;
  invisible: boolean;
  hideDependencyLines: boolean;
  dependencies: string[];
  tasks: QuestTask[];
  rewards: QuestReward[];
  /** Original parsed object — reserved for a future editor. */
  raw: Record<string, unknown>;
}

export interface QuestChapterModel {
  id: string;
  title: string;
  filename: string | null;
  groupId: string | null;
  defaultShape: QuestShape;
  defaultHideDependencyLines: boolean;
  quests: QuestNode[];
  questById: Map<string, QuestNode>;
  edges: { from: string; to: string }[];
  bounds: { minX: number; minY: number; maxX: number; maxY: number };
  iconItemIds: string[];
}

function parseDependencies(raw: Record<string, unknown>): string[] {
  const fromArray = unwrapStringList(raw.dependencies ?? raw.dependency);
  const unique = new Set<string>();
  for (const id of fromArray) unique.add(id);
  return Array.from(unique);
}

function parseDescription(value: unknown): string[] {
  return unwrapStringList(value);
}

function parseTask(value: unknown): QuestTask | null {
  const raw = asRecord(value);
  if (!raw) return null;
  const type = unwrapString(raw.type) ?? unwrapString(raw.id) ?? 'unknown';
  return {
    id: unwrapString(raw.id),
    type,
    title: unwrapString(raw.title),
    itemId: firstItemId(raw.item ?? raw.items ?? raw.icon ?? raw),
    count: raw.count != null || raw.value != null ? unwrapNumber(raw.count ?? raw.value, 1) : null,
    raw,
  };
}

function parseReward(value: unknown): QuestReward | null {
  const raw = asRecord(value);
  if (!raw) return null;
  const type = unwrapString(raw.type) ?? 'unknown';
  return {
    id: unwrapString(raw.id),
    type,
    title: unwrapString(raw.title),
    itemId: firstItemId(raw.item ?? raw.items ?? raw.icon ?? raw),
    count: raw.count != null ? unwrapNumber(raw.count, 1) : null,
    raw,
  };
}

function firstTaskItemId(tasks: QuestTask[]): string | null {
  for (const task of tasks) {
    if (task.itemId) return task.itemId;
  }
  return null;
}

function parseQuest(value: unknown, index: number, defaultShape: QuestShape): QuestNode | null {
  const raw = asRecord(value);
  if (!raw) return null;
  const id = unwrapString(raw.id) ?? `quest_${index}`;
  const title = unwrapString(raw.title) ?? id;
  const tasks = asArray(raw.tasks).map(parseTask).filter((t): t is QuestTask => t != null);
  return {
    id,
    title,
    subtitle: unwrapString(raw.subtitle),
    description: parseDescription(raw.description),
    // Circle / sidebar icon: first item on any task. Quest `icon` is only a fallback
    // when every task is item-less (checkmark, location, …).
    iconItemId: firstTaskItemId(tasks) ?? extractItemId(raw.icon ?? raw.item),
    x: unwrapNumber(raw.x, 0),
    y: unwrapNumber(raw.y, 0),
    size: Math.max(0.35, unwrapNumber(raw.size, 1)),
    shape: unwrapString(raw.shape) || defaultShape,
    optional: unwrapBoolean(raw.optional, false),
    invisible: unwrapBoolean(raw.invisible, false),
    hideDependencyLines: unwrapBoolean(raw.hide_dependency_lines, false),
    dependencies: parseDependencies(raw),
    tasks,
    rewards: asArray(raw.rewards).map(parseReward).filter((r): r is QuestReward => r != null),
    raw,
  };
}

export function displayQuestTitle(title: unknown, fallback = 'Untitled'): string {
  // Titles are optional in SNBT and the public API preserves them as null.
  const text = typeof title === 'string' ? title.replace(/§[0-9a-fk-or]/gi, '').trim() : '';
  if (!text) return fallback;
  if (text.startsWith('{') && text.endsWith('}')) {
    const inner = text.slice(1, -1);
    const last = inner.split('.').pop();
    if (last) return last.replace(/_/g, ' ');
  }
  return text;
}

export function buildChapterModel(parsed: unknown): QuestChapterModel {
  const root = asRecord(parsed) ?? {};
  const defaultShape = unwrapString(root.default_quest_shape) || 'circle';
  const defaultHide = unwrapBoolean(root.default_hide_dependency_lines, false);
  const quests: QuestNode[] = [];
  asArray(root.quests).forEach((entry, i) => {
    const q = parseQuest(entry, i, defaultShape);
    if (q) quests.push(q);
  });

  const questById = new Map<string, QuestNode>();
  for (const q of quests) questById.set(q.id, q);

  const edges: { from: string; to: string }[] = [];
  for (const q of quests) {
    if (q.hideDependencyLines || defaultHide) continue;
    for (const dep of q.dependencies) {
      if (questById.has(dep)) edges.push({ from: dep, to: q.id });
    }
  }

  let minX = 0;
  let minY = 0;
  let maxX = 0;
  let maxY = 0;
  if (quests.length > 0) {
    minX = Infinity;
    minY = Infinity;
    maxX = -Infinity;
    maxY = -Infinity;
    for (const q of quests) {
      const r = q.size * 0.6;
      if (q.x - r < minX) minX = q.x - r;
      if (q.y - r < minY) minY = q.y - r;
      if (q.x + r > maxX) maxX = q.x + r;
      if (q.y + r > maxY) maxY = q.y + r;
    }
  }

  const iconItemIds = new Set<string>();
  for (const q of quests) {
    if (q.iconItemId) iconItemIds.add(q.iconItemId);
    for (const t of q.tasks) if (t.itemId) iconItemIds.add(t.itemId);
    for (const r of q.rewards) if (r.itemId) iconItemIds.add(r.itemId);
  }

  return {
    id: unwrapString(root.id) ?? 'chapter',
    title: unwrapString(root.title) ?? unwrapString(root.filename) ?? 'Chapter',
    filename: unwrapString(root.filename),
    groupId: unwrapString(root.group),
    defaultShape,
    defaultHideDependencyLines: defaultHide,
    quests,
    questById,
    edges,
    bounds: { minX, minY, maxX, maxY },
    iconItemIds: Array.from(iconItemIds),
  };
}

export interface ChapterGroupModel {
  id: string;
  title: string;
}

export function buildChapterGroupsModel(parsed: unknown): ChapterGroupModel[] {
  const root = asRecord(parsed) ?? {};
  const groups = asArray(root.chapter_groups);
  const out: ChapterGroupModel[] = [];
  for (const entry of groups) {
    const rec = asRecord(entry);
    if (!rec) continue;
    const id = unwrapString(rec.id);
    if (!id) continue;
    out.push({ id, title: unwrapString(rec.title) ?? id });
  }
  return out;
}
